// src/components/ProductImportModal.jsx
import React, { useState } from 'react';
import { Modal, Button, Upload, message, Tooltip, Row, Col, Alert } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useMutation, useLazyQuery } from '@apollo/client';
import { IMPORT_PRODUCTS, GET_PRODUCT_IMPORT_TEMPLATE } from '../mutations/importProducts';

const ProductImportModal = ({ visible, onCancel, refetchProducts, resetToFirstPage }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [importProducts] = useMutation(IMPORT_PRODUCTS);
  const [getProductTemplate] = useLazyQuery(GET_PRODUCT_IMPORT_TEMPLATE);

  const handleUpload = async () => {
    const file = fileList[0];
    if (!file) {
      message.error('Por favor, selecione um arquivo.');
      return;
    }

    setUploading(true);
    try {
      const { data } = await importProducts({
        variables: { file: file.originFileObj },
        refetchQueries: ['GetProducts'],
        awaitRefetchQueries: true
      });
      message.success(data.importProducts.message);
      
      // Resetar para primeira página se a função foi passada
      if (resetToFirstPage) {
        resetToFirstPage();
      }
      
      // Aguardar o refetch antes de fechar o modal
      if (refetchProducts) {
        await refetchProducts();
      }
      
      // Limpar a lista de arquivos
      setFileList([]);
      onCancel();
    } catch (error) {
      message.error('Falha no upload.');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const downloadExampleFile = async () => {
    setDownloading(true);
    
    try {
      const { data } = await getProductTemplate();
      
      if (data && data.productImportTemplate) {
        // Converter base64 em blob
        const byteCharacters = atob(data.productImportTemplate);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Criar URL e fazer download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'template_importacao_produtos.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        message.error('Erro ao obter o arquivo de exemplo');
      }
    } catch (error) {
      message.error('Falha ao baixar o arquivo de exemplo');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title="Importar Produtos"
      onCancel={onCancel}
      maskClosable={false}
      width={800}
      translate="no"
      className="notranslate"
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" onClick={handleUpload} disabled={fileList.length === 0} loading={uploading}>
          {uploading ? 'Enviando' : 'Enviar'}
        </Button>,
      ]}
    >
      <p>
        Importe seus produtos através de uma planilha Excel (.xlsx). A planilha deve conter as seguintes colunas:
      </p>
      <ul>
        <li><strong>Referência*</strong> - Código único do produto</li>
        <li><strong>Nome*</strong> - Nome completo do produto</li>
        <li><strong>Marca*</strong> - Marca do produto</li>
        <li><strong>Grupo*</strong> - Categoria ou grupo do produto</li>
        <li><strong>Unidade*</strong> - Unidade de medida (UN, KG, etc.)</li>
        <li><strong>Valor*</strong> - Preço de venda</li>
        <li><strong>Multiplicador*</strong> - Valor multiplicador (padrão: 1.0)</li>
        <li><strong>Estoque*</strong> - Quantidade disponível</li>
        <li><strong>Valor Mínimo*</strong> - Menor preço aceitável</li>
        <li><strong>Valor de Custo*</strong> - Custo de aquisição</li>
      </ul>
      <p><strong>* Campos obrigatórios</strong></p>
      
      <p><strong>Exemplo:</strong></p>
      <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ backgroundColor: '#f0f0f0' }}>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>referência</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>nome</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>marca</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>grupo</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>un</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>valor</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>multiplicador</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>estoque</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>valor_mínimo</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>valor_custo</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>REF001</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Produto 1</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Marca A</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>P1</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>UN</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>10,5</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>1</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>100</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>5</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>8</td>
            </tr>
            <tr style={{ backgroundColor: '#f9f9f9' }}>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>REF002</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Produto 2</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Marca B</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>P1</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>UN</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>15</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>2</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>200</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>7,5</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>12</td>
            </tr>
          </tbody>
        </table>
      </div>

      <Row align="middle" style={{ marginTop: 16 }}>
        <Col>
          <Upload
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
            accept=".xlsx"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Selecione o arquivo</Button>
          </Upload>
        </Col>
        <Col>
          <Tooltip title="Baixe um exemplo de planilha de importação">
            <DownloadOutlined 
              onClick={downloadExampleFile} 
              style={{ marginLeft: 16, fontSize: '24px', color: '#ffc107', cursor: 'pointer' }}
              spin={downloading}
            />
          </Tooltip>
        </Col>
      </Row>
    </Modal>
  );
};

export default ProductImportModal;
